/**
 * The phone's navigation: a bottom bar, not a drawer.
 *
 * A hamburger hides everything behind a tap and a wait, and puts the one control the
 * office reaches for most — the scanner — three interactions away while they are
 * standing at the store door holding a tool. A bottom bar puts four destinations and
 * the scanner under the thumb, which is the half of the screen a thumb actually
 * reaches.
 *
 * Five slots, and the middle one is the scanner. It is raised and in brand red because
 * it is not a peer of the others: the other four are places you go, this is a thing you
 * do, and it is the reason the portal is installed on a phone at all.
 *
 * "More" opens a sheet with everything else rather than a second bar. Thirteen
 * destinations do not fit five slots and never will, and a sheet you can read in one
 * glance beats a bar that scrolls sideways.
 *
 * Hidden from `lg` up, where the sidebar is permanently visible and this would be a
 * second copy of it.
 */

import { useEffect, useState } from 'react';
import type { PermissionLevel, Section } from '@/lib/api';
import { NavLink, useLocation } from 'react-router-dom';

import { useI18n, type TranslationKey } from '@/i18n';

import {
  IconAlerts,
  IconAssignments,
  IconAudit,
  IconCalendar,
  IconLeave,
  IconCompliance,
  IconDashboard,
  IconDocuments,
  IconMessages,
  IconEmployees,
  IconInventory,
  IconVehicles,
  IconJobs,
  IconPayroll,
  IconRecycleBin,
  IconShifts,
  IconSites,
} from './NavIcons';

interface Destination {
  to: string;
  labelKey: TranslationKey;
  icon: (props: { className?: string }) => JSX.Element;
  end?: boolean;
  section: Section;
}

/*
 * FIVE slots, and the scanner is the third of five.
 *
 * This started as two destinations either side of the scanner plus More, which is six
 * slots — and the middle of six is a gap between two of them, so the raised button sat
 * visibly off to the left. Two, scanner, one, More is what actually centres it.
 *
 * Which four: what the office opens standing up rather than sitting down. Where
 * everyone is today, the register the scanner lands in, and the dashboard as the way
 * home. Jobs is a sit-down task, so it moved under More.
 */
const PRIMARY: Destination[] = [
  { to: '/', section: 'dashboard', labelKey: 'nav.dashboard', icon: IconDashboard, end: true },
  { to: '/shifts', section: 'shifts', labelKey: 'nav.shifts', icon: IconShifts },
];

const SECONDARY: Destination[] = [
  { to: '/inventory', section: 'inventory', labelKey: 'nav.inventory', icon: IconInventory },
  { to: '/vehicles', section: 'vehicles', labelKey: 'nav.vehicles', icon: IconVehicles },
];

const MORE: { titleKey: TranslationKey | null; items: Destination[] }[] = [
  {
    titleKey: 'nav.groupOperations',
    items: [
      { to: '/calendar', section: 'calendar', labelKey: 'nav.calendar', icon: IconCalendar },
      { to: '/leave', section: 'leave', labelKey: 'nav.leave', icon: IconLeave },
      { to: '/assignments', section: 'assignments', labelKey: 'nav.assignments', icon: IconAssignments },
      { to: '/payroll', section: 'payroll', labelKey: 'nav.payroll', icon: IconPayroll },
    ],
  },
  {
    titleKey: 'nav.groupRecords',
    items: [
      { to: '/jobs', section: 'jobs', labelKey: 'nav.jobs', icon: IconJobs },
      { to: '/employees', section: 'employees', labelKey: 'nav.employees', icon: IconEmployees },
      { to: '/sites', section: 'sites', labelKey: 'nav.sites', icon: IconSites },
    ],
  },
  {
    titleKey: 'nav.groupCompliance',
    items: [
      { to: '/alerts', section: 'alerts', labelKey: 'nav.alerts', icon: IconAlerts },
      { to: '/compliance', section: 'compliance', labelKey: 'nav.compliance', icon: IconCompliance },
      { to: '/documents', section: 'documents', labelKey: 'nav.documents', icon: IconDocuments },
      { to: '/messages', section: 'messages', labelKey: 'nav.messages', icon: IconMessages },
    ],
  },
  {
    titleKey: 'nav.groupSystem',
    items: [
      { to: '/recycle-bin', section: 'recycle_bin', labelKey: 'nav.recycleBin', icon: IconRecycleBin },
      { to: '/audit', section: 'audit', labelKey: 'nav.audit', icon: IconAudit },
    ],
  },
];

const TAB =
  'flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 h-full text-[11px] font-semibold transition-colors';

function Tab({ destination }: { destination: Destination }) {
  const { t } = useI18n();
  const Icon = destination.icon;

  return (
    <NavLink
      to={destination.to}
      end={destination.end}
      className={({ isActive }) => `${TAB} ${isActive ? 'text-primary' : 'text-muted'}`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span className="truncate max-w-full px-1">{t(destination.labelKey)}</span>
    </NavLink>
  );
}

export function BottomNav({
  can,
  alertCount,
}: {
  can: (section: Section, level: PermissionLevel) => boolean;
  alertCount: number;
}) {
  const { t } = useI18n();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  // Going somewhere should get you there, not leave you looking at the sheet you tapped
  // the link in.
  useEffect(() => setMoreOpen(false), [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMoreOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  return (
    <>
      {moreOpen ? (
        <div className="lg:hidden fixed inset-0 z-40 flex flex-col justify-end">
          <button
            type="button"
            aria-label={t('nav.closeMenu')}
            className="absolute inset-0 bg-black/40"
            onClick={() => setMoreOpen(false)}
          />

          {/* Rises from the bar it was opened from, and stops short of the top: the
              page staying visible above it is what says this is a layer, not a page. */}
          <div className="relative bg-surface rounded-t-xl border-t border-border max-h-[75dvh] overflow-y-auto pb-[env(safe-area-inset-bottom)]">
            <div className="sticky top-0 bg-surface flex items-center gap-3 px-4 py-3 border-b border-border">
              <h2 className="font-bold flex-1">{t('nav.more')}</h2>
              <button
                type="button"
                className="btn-secondary px-2.5"
                onClick={() => setMoreOpen(false)}
              >
                {t('common.close')}
              </button>
            </div>

            <div className="p-2">
              {MORE.map((group) => {
                const items = group.items.filter((i) => can(i.section, 'read'));
                if (items.length === 0) return null;

                return (
                  <div key={group.titleKey ?? 'main'} className="mb-2 last:mb-0">
                    {group.titleKey ? (
                      <div className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">
                        {t(group.titleKey)}
                      </div>
                    ) : null}

                    {items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-3 rounded text-sm font-semibold ${
                              isActive ? 'text-primary bg-[var(--color-primary-soft)]' : 'text-text'
                            }`
                          }
                        >
                          <Icon className="w-5 h-5 shrink-0" />
                          <span className="flex-1">{t(item.labelKey)}</span>
                          {item.to === '/alerts' && alertCount > 0 ? (
                            <span className="text-xs font-bold text-primary tabular">
                              {alertCount}
                            </span>
                          ) : null}
                        </NavLink>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/*
        `overflow-visible` matters: the scanner is taller than the row it sits in, and
        a bar that clips its own content cuts the bottom off the one control this
        whole layout exists for.

        `env(safe-area-inset-bottom)` keeps the row clear of the iPhone home indicator,
        which otherwise sits on top of that same button.
      */}
      <nav
        aria-label={t('nav.portal')}
        className="lg:hidden shrink-0 border-t border-border bg-surface overflow-visible pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-stretch h-14 overflow-visible">
          {PRIMARY.map((d) => (
            <Tab key={d.to} destination={d} />
          ))}

          {/* Not a peer of the others: a thing you do, not a place you go.

              Positioned rather than margin-shifted. A negative margin inside a flex row
              moves the box AND leaves its height in the layout, so the circle hung past
              the bottom of the bar; absolute centring from the row's own top edge puts
              it where it looks like it should be, whatever the row's height. */}
          <div className="flex-1 min-w-0 relative">
            <NavLink
              to="/inventory/scan"
              aria-label={t('inventory.scanTitle')}
              className="absolute left-1/2 -translate-x-1/2 -top-4 w-14 h-14 rounded-full bg-primary text-[var(--color-on-primary)] grid place-items-center shadow-[var(--shadow-md)] border-4 border-[var(--color-bg)]"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16" />
                <path d="M4 12h16" />
              </svg>
            </NavLink>
          </div>

          {SECONDARY.map((d) => (
            <Tab key={d.to} destination={d} />
          ))}

          <button
            type="button"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen(true)}
            className={`${TAB} ${moreOpen ? 'text-primary' : 'text-muted'}`}
          >
            <span className="relative">
              <svg
                viewBox="0 0 20 20"
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden
              >
                <path d="M3 6h14M3 10h14M3 14h14" />
              </svg>
              {/* The only badge on the bar. An alert sitting under More is an alert
                  nobody would otherwise see. */}
              {alertCount > 0 ? (
                <span className="absolute -top-1 -end-1.5 w-2 h-2 rounded-full bg-[var(--color-danger)]" />
              ) : null}
            </span>
            <span className="truncate max-w-full px-1">{t('nav.more')}</span>
          </button>
        </div>
      </nav>
    </>
  );
}
